import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Mail, AlertCircle, Eye, EyeOff } from "lucide-react";

export function Login() {
	const navigate = useNavigate();
	const location = useLocation();
	const { login, isAuthenticated, isLoading, error, clearError } =
		useAuthStore();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [localError, setLocalError] = useState("");

	// Redirect if already authenticated
	useEffect(() => {
		if (isAuthenticated) {
			const from = (location.state as any)?.from?.pathname || "/dashboard";
			navigate(from, { replace: true });
		}
	}, [isAuthenticated, navigate, location]);

	// Clear error when inputs change
	useEffect(() => {
		if (error || localError) {
			clearError();
			setLocalError("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [email, password]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Basic validation
		if (!email.trim()) {
			setLocalError("Email is required");
			return;
		}
		if (!password) {
			setLocalError("Password is required");
			return;
		}

		const success = await login(email.trim(), password);
		if (success) {
			const from = (location.state as any)?.from?.pathname || "/dashboard";
			navigate(from, { replace: true });
		}
	};

	const displayError = localError || error;

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
			<div className="w-full max-w-md">
				{/* Logo/Brand */}
				<div className="text-center mb-8">
					<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 mb-4">
						<Lock className="w-8 h-8 text-white" />
					</div>
					<h1 className="text-3xl font-bold text-white">Flow Metrics</h1>
					<p className="text-gray-400 mt-2">System Admin Portal</p>
				</div>

				{/* Login Card */}
				<Card className="border-0 shadow-2xl">
					<CardHeader className="space-y-1 pb-4">
						<CardTitle className="text-2xl text-center">Sign In</CardTitle>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSubmit} className="space-y-4">
							{/* Error Message */}
							{displayError && (
								<div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
									<AlertCircle className="w-4 h-4 flex-shrink-0" />
									<span>{displayError}</span>
								</div>
							)}

							{/* Email Field */}
							<div className="space-y-2">
								<label
									htmlFor="email"
									className="text-sm font-medium text-gray-700"
								>
									Email
								</label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
									<Input
										id="email"
										type="email"
										placeholder="admin@example.com"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										className="pl-10"
										autoComplete="email"
										autoFocus
									/>
								</div>
							</div>

							{/* Password Field */}
							<div className="space-y-2">
								<label
									htmlFor="password"
									className="text-sm font-medium text-gray-700"
								>
									Password
								</label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
									<Input
										id="password"
										type={showPassword ? "text" : "password"}
										placeholder="Enter your password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className="pl-10 pr-10"
										autoComplete="current-password"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
									>
										{showPassword ? (
											<EyeOff className="w-5 h-5" />
										) : (
											<Eye className="w-5 h-5" />
										)}
									</button>
								</div>
							</div>

							{/* Submit Button */}
							<Button
								type="submit"
								className="w-full bg-blue-600 hover:bg-blue-700"
								disabled={isLoading}
							>
								{isLoading ? (
									<div className="flex items-center gap-2">
										<div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
										Signing in...
									</div>
								) : (
									"Sign In"
								)}
							</Button>
						</form>
					</CardContent>
				</Card>

				{/* Footer */}
				<p className="text-center text-gray-500 text-sm mt-6">
					Protected system access. Unauthorized access is prohibited.
				</p>
			</div>
		</div>
	);
}
